import React, { useEffect, useRef, useState } from "react";
import { MessageSquarePlus, Send, Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import AssistantAnswer from "./AssistantAnswer";
import AssistantMarkdown from "./AssistantMarkdown";
import { useAssistantResize } from "../../hooks/useAssistantResize";
import styles from "../../OrderAnalyticsPage.module.scss";

export default function AnalyticsAssistant({ chat, period }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const input = useRef(null);
  const body = useRef(null);
  const panel = useRef(null);
  const resize = useAssistantResize(panel);
  const suggestions = [
    [t("analyticsNew.assistant.carriers", "Кто быстрее всех отвечает?"), "fastest-responding-companies"],
    [t("analyticsNew.assistant.overview", "Обзор заказов"), "orders-overview"],
    [
      t("analyticsNew.assistant.assignmentQuestion", "Сколько прямым пробросом и через тендер?"),
      "orders-by-tender-type",
    ],
    [t("analyticsNew.assistant.routes", "Самые частые маршруты"), "route-analytics"],
  ];
  useEffect(() => {
    setDraft("");
  }, [period.startDate, period.endDate]);
  useEffect(() => {
    if (body.current) body.current.scrollTop = body.current.scrollHeight;
  }, [chat.messages]);
  function submit(event) {
    event.preventDefault();
    if (!draft.trim() || chat.pending || !chat.validPeriod) return;
    chat.ask(draft);
    setDraft("");
    input.current?.focus();
  }
  return (
    <aside
      ref={panel}
      id="analytics-assistant-panel"
      className={`${styles.chat} ${resize.dragging ? styles.chatResizing : ""}`}
      style={{ "--assistant-width": `${resize.width}px` }}
      aria-labelledby="analytics-assistant-title"
    >
      <div
        className={styles.chatResizeHandle}
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-controls="analytics-assistant-panel"
        aria-label={t("analyticsNew.assistant.resize", "Изменить ширину чата")}
        aria-valuemin={resize.bounds.min}
        aria-valuemax={resize.bounds.max}
        aria-valuenow={resize.width}
        title={t(
          "analyticsNew.assistant.resizeHint",
          "Потяните влево, чтобы расширить чат. Двойной клик — исходная ширина.",
        )}
        {...resize.handleProps}
      >
        <span aria-hidden="true" />
      </div>
      <div className={styles.chatHead}>
        <div className={styles.aiDot} aria-hidden="true">
          Y
        </div>
        <div className={styles.chatHeading}>
          <h2 id="analytics-assistant-title">{t("analyticsNew.assistant.title", "Ассистент binY")}</h2>
          <p>{t("analyticsNew.assistant.subtitle", "Детали по заказам, ТК и маршрутам")}</p>
        </div>
        <button
          type="button"
          className={styles.newChat}
          aria-label={t("analyticsNew.assistant.newChat", "Новый чат")}
          title={t("analyticsNew.assistant.newChat", "Новый чат")}
          onClick={() => {
            chat.newChat();
            setDraft("");
            input.current?.focus();
          }}
        >
          <MessageSquarePlus size={18} aria-hidden="true" />
        </button>
      </div>
      <div className={styles.chatBody} ref={body}>
        <div className={styles.botMessage}>
          {t(
            "analyticsNew.assistant.readyGreeting",
            "Привет! Слева — общая картинка. Здесь можно узнать подробности о заказах, перевозчиках и маршрутах за выбранные даты.",
          )}
          <div className={styles.chips}>
            {suggestions.map(([question, tool]) => (
              <button
                type="button"
                className={styles.chip}
                key={question}
                disabled={chat.pending || !chat.validPeriod}
                onClick={() => chat.ask(question, { tool })}
              >
                {question}
              </button>
            ))}
            <button
              type="button"
              className={styles.chip}
              disabled={chat.pending || !chat.validPeriod}
              onClick={() => {
                setDraft(t("analyticsNew.assistant.companyPrefix", "Расскажи про "));
                input.current?.focus();
              }}
            >
              {t("analyticsNew.assistant.companyQuestion", "Узнать о перевозчике")}
            </button>
          </div>
        </div>
        <div
          className={styles.chatMessages}
          role="log"
          aria-label={t("analyticsNew.assistant.history", "История сообщений")}
          aria-live="polite"
          aria-relevant="additions text"
        >
          {chat.messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <div className={styles.userMessage}>{message.question}</div>
              {(message.text || message.status !== "pending") && (
                <div className={styles.botMessage}>
                  {message.status === "success" && message.request && (
                    <AssistantAnswer request={message.request} data={message.data} />
                  )}
                  {message.text && (
                    <AssistantMarkdown text={message.text} streaming={message.status === "pending"} />
                  )}
                  {message.status === "error" && (
                    <p>
                      {t("analyticsNew.assistant.error", "Не удалось получить ответ. Попробуйте ещё раз.")}
                    </p>
                  )}
                  {message.status === "stopped" && (
                    <p className={styles.answerSource}>
                      {t("analyticsNew.assistant.stopped", "Ответ остановлен")}
                    </p>
                  )}
                  {message.status === "interrupted" && (
                    <p>
                      {t(
                        "analyticsNew.assistant.interrupted",
                        "Агент запросил дополнительное действие, которое пока не поддерживается в этом чате.",
                      )}
                    </p>
                  )}
                  {index === chat.messages.length - 1 && ["error", "stopped"].includes(message.status) && (
                    <button
                      type="button"
                      className={styles.chip}
                      disabled={chat.pending || !chat.validPeriod}
                      onClick={() => chat.retry(message)}
                    >
                      {t("analyticsNew.retry", "Повторить")}
                    </button>
                  )}
                </div>
              )}
              {message.status === "pending" && (
                <div className={styles.typing} role="status">
                  <span className={styles.typingLabel}>
                    {message.phase === "writing"
                      ? t("analyticsNew.assistant.writing", "Пишу ответ…")
                      : message.phase === "querying"
                        ? t("analyticsNew.assistant.querying", "Получаю данные…")
                        : t("analyticsNew.assistant.loading", "Готовлю ответ…")}
                  </span>
                  <i aria-hidden="true" />
                  <i aria-hidden="true" />
                  <i aria-hidden="true" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <p className={styles.chatNotice} id="analytics-assistant-period">
        {chat.validPeriod
          ? `${t("analyticsNew.assistant.period", "Период")}: ${period.startDate.split("-").reverse().join(".")} — ${period.endDate.split("-").reverse().join(".")}`
          : t("analyticsNew.requiredDates", "Укажите начало и конец периода")}
      </p>
      <form
        className={styles.chatInput}
        onSubmit={submit}
        aria-label={t("analyticsNew.assistant.form", "Вопрос ассистенту")}
      >
        <input
          ref={input}
          aria-label={t("analyticsNew.assistant.message", "Сообщение ассистенту")}
          aria-describedby="analytics-assistant-period"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("analyticsNew.assistant.placeholder", "Например: кто быстрее всех отвечает?")}
          autoComplete="off"
          maxLength={1000}
          disabled={!chat.validPeriod}
        />
        {chat.pending ? (
          <button
            type="button"
            onClick={chat.stop}
            aria-label={t("analyticsNew.assistant.stop", "Остановить ответ")}
          >
            <Square size={18} aria-hidden="true" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!draft.trim() || !chat.validPeriod}
            aria-label={t("analyticsNew.assistant.send", "Отправить")}
          >
            <Send size={18} aria-hidden="true" />
          </button>
        )}
      </form>
    </aside>
  );
}
